import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const I18nContext = createContext();

// Default English translations (fallback)
const DEFAULT_TRANSLATIONS = {
  welcome: "Welcome to Uncharted Travels",
  chat_with_lauren: "Chat with Lauren",
  search_flights: "Search Flights",
  search_hotels: "Search Hotels",
  my_trips: "My Trips",
  settings: "Settings",
  login: "Login",
  signup: "Create Account",
  logout: "Logout",
  budget: "Budget",
  travelers: "Travelers",
  departure: "Departure",
  return: "Return",
  book_now: "Book Now",
  add_to_plan: "Add to Plan",
  view_details: "View Details",
  total_cost: "Total Cost",
  per_person: "per person",
  per_night: "per night",
  direct_flight: "Direct",
  stops: "stop(s)",
  activities: "Activities",
  accommodation: "Accommodation",
  flights: "Flights",
  food_advisory: "Food & Dining (Advisory)",
  on_demand_booking: "You only pay when you confirm",
  session_expiring: "Session expires soon",
  session_expired: "Session expired. Please log in again.",
  booking_confirmed: "Booking Confirmed",
  cancellation_policy: "Cancellation Policy",
  free_cancellation: "Free cancellation",
  group_trip: "Group Trip",
  invite_members: "Invite Members",
  share_trip: "Share Trip",
  flight_status: "Flight Status",
  explore_destinations: "Explore Destinations",
  popular_destinations: "Popular Destinations",
  featured_experiences: "Featured Experiences",
  // Navigation
  home: "Home",
  search: "Search",
  chat: "Chat",
  cart: "Cart",
  favorites: "Favorites",
  bookings: "Bookings",
  support: "Support",
  more: "More",
  // Settings
  appearance: "Appearance",
  theme: "Theme",
  light: "Light",
  dark: "Dark",
  brand: "Brand",
  explorer: "Explorer",
  profile: "Profile",
  notifications: "Notifications",
  travel_preferences: "Travel Preferences",
  preferred_currency: "Preferred Currency",
  language: "Language",
  save_changes: "Save Changes",
  reset: "Reset",
  // Auth
  welcome_back: "Welcome Back",
  sign_in_continue: "Sign in to continue your travel journey",
  create_account: "Create Account",
  join_community: "Join the world's most exclusive travel community",
  username: "Username",
  email: "Email",
  password: "Password",
  confirm_password: "Confirm Password",
  full_name: "Full Name",
  sign_in: "Sign In",
  dont_have_account: "Don't have an account?",
  already_have_account: "Already have an account?",
  // Common
  loading: "Loading...",
  error: "Error",
  success: "Success",
  cancel: "Cancel",
  confirm: "Confirm",
  close: "Close",
  back: "Back",
  next: "Next",
  save: "Save",
  delete: "Delete",
  // Currency alerts
  currency_alerts: "Currency Alerts",
  set_alert: "Set Alert",
  when_rate: "When rate",
  drops_below: "drops below",
  rises_above: "rises above",
  // Visa
  visa_prefill: "Visa Pre-fill",
  passport_data: "Passport Data",
  generate_form: "Generate Form",
  // Itinerary
  daily_planner: "Daily Planner",
  add_activity: "Add Activity",
  day: "Day",
};

const LANGUAGE_NAMES = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
  nl: "Nederlands",
  ja: "日本語",
  ko: "한국어",
  zh: "中文",
  ar: "العربية",
  tr: "Türkçe",
};

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('app_language') || 'en';
  });
  const [translations, setTranslations] = useState(DEFAULT_TRANSLATIONS);

  const loadTranslations = useCallback(async (lang) => {
    if (lang === 'en') {
      setTranslations(DEFAULT_TRANSLATIONS);
      return;
    }
    try {
      const res = await fetch(`${API}/i18n/translations/${lang}`);
      if (res.ok) {
        const data = await res.json();
        // Merge with defaults so untranslated keys fall back to English
        setTranslations({ ...DEFAULT_TRANSLATIONS, ...data.translations });
      }
    } catch {
      // Keep defaults on error
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('app_language', language);
    loadTranslations(language);
    // Set HTML lang attribute
    document.documentElement.lang = language;
    // Set direction for RTL languages
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language, loadTranslations]);

  const t = useCallback((key, fallback) => {
    return translations[key] || fallback || key;
  }, [translations]);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
  }, []);

  return (
    <I18nContext.Provider value={{ t, language, changeLanguage, languages: LANGUAGE_NAMES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return { t: (key) => key, language: 'en', changeLanguage: () => {}, languages: LANGUAGE_NAMES };
  }
  return ctx;
}
